import React, { useEffect } from "react";
import { Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Header } from "../Header";
import { AppointmentSidebar } from "../AppointmentSidebar";
import { ProgressBar } from "../ProgressBar";
import { captureException, captureMessage } from "@sentry/react";
import { useShouldHideCloseButton } from "src/hooks/shouldHideCloseButton";
import { usePageTitle } from "src/hooks/usePageTitle";
import { useSelector } from "src/store";

export function BookingFlowLayout({ children }: React.PropsWithChildren) {
    const { clinicId, doctorErpId, appointmentTypeId } = useSelector(state => state.appointment);
    const hideCloseButton = useShouldHideCloseButton();
    usePageTitle();
    const [search] = useSearchParams();
    const { pathname } = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        if (search.has('fallback')) {
            captureException("Opened fallback", {
                level: "info",
                extra: {
                    fallback: search.get('fallback')
                }
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!clinicId || !doctorErpId || !appointmentTypeId) {
            captureMessage(`No data for ${Object.entries({
                'Clinic ID': !!clinicId,
                'Doctor ID': !!doctorErpId,
                'Appointment Type': !!appointmentTypeId
            }).filter(([_, v]) => !v).map(([k, _]) => k).join(', ')}.`);

            // TODO future-proof routes
            if ([
                // routes where appt is certainly completed/confirmed
                'app/consent',
                'app/additional-details',
            ].indexOf(pathname.replaceAll(/^\/|\/$/g, '')) > -1) {
                navigate('/error/no-session-data/confirmed');
            } else {
                navigate('/error/no-session-data');
            }
        }
    }, [clinicId, doctorErpId, appointmentTypeId, navigate, pathname])

    return <div className="flex flex-col min-h-dvh">
        <Header progressBar hideCloseButton={hideCloseButton} className="hidden lg:block" />
        <div className="flex-1 flex flex-col lg:flex-row">
            <aside className="lg:max-w-md w-full bg-gray-50 border-r border-gray-200 p-4 lg:p-8">
                <AppointmentSidebar />
            </aside>
            <ProgressBar className="block lg:hidden" />
            <main className="flex-1 lg:py-8 bg-white relative flex">
                <div className="max-w-lg mx-auto 2xl:mx-72 pt-5 flex flex-col flex-1">
                    <Outlet />
                    {children}
                </div>
            </main>
        </div>
    </div>
}